/**
 * @category LeftSide
 * @component
 * @module Files/fileProcessing/ProcessingOptions
 * @description This component displays options that user can select for file processing. It combines other
 * components responsible for some of the file loading functionality and provides a way to save/load presets.
 */
import React, { useState, useContext, useEffect, useRef } from "react";
import {
  // Button,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Checkbox,
  TextField,
} from "@mui/material";
import units from "../../../../utils/units";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PresetLoader from "./PresetLoader";
import { DashboardContext } from "../../../../context/DashboardContext";
import { GeneralContext } from "../../../../context/GeneralContext";
import {
  useTutorialProgress,
  useTutorial,
} from "../../../../context/TutorialContext";
import "./fileProcessing.scss";
import CustomButton from "../../../commonComponents/CustomButton";
import HtmlTooltip from "../../../commonComponents/HtmlTooltip";
import { isCompleteNumber, hasProperty } from '../../../../utils/helpers';

function ProcessingOptions(props) {
  const {
    save,
    apply,
    applyAll,
    check,
    modalId,
    cancel,
    fileContent,
    processedFileDetails,
    single = false,
    processedContent = null,
    initialOptions = {},
    onOptionsChange,
    fileIndex,
    fileName,
  } = props;
  const { filePresetsLoadingOptions, setfilePresetsLoadingOptions, defaultXAxisUnit } = useContext(DashboardContext);
  const { recordedErrorLog } = useContext(GeneralContext);
  const { monitorStateChange } = useTutorialProgress();
  const { currentTutorial } = useTutorial();
  const [colCount, setColCount] = useState(2);
  const [colCountInput, setColCountInput] = useState("2");
  const [separator, setSeparator] = useState("whitespace");
  // const [col1, setCol1] = useState("x");
  // const [col2, setCol2] = useState("y");
  // const [col3, setCol3] = useState("e");
  const [autoRemove, setAutoRemove] = useState(true);
  const [manualLineStart, setManualLineStart] = useState(0);
  const [xUnit, setXUnit] = useState(
    single && initialOptions ? initialOptions.xUnit : defaultXAxisUnit
  );
  
  // Convert initial options if they contain "val-x" mode
  const convertInitialOptions = (options) => {
    if (!options) return options;
    
    const converted = { ...options };
    
    // Convert X shift
    if (options.xShift === "val-x") {
      converted.xShift = "x+val";
      converted.xShiftVal = options.xShiftVal ? (parseFloat(options.xShiftVal) * -1).toString() : "";
    }
    
    // Convert Y shift
    if (options.yShift === "val-x") {
      converted.yShift = "x+val";
      converted.yShiftVal = options.yShiftVal ? (parseFloat(options.yShiftVal) * -1).toString() : "";
    }

    if (!hasProperty(converted, "xShiftUnit")) {
      converted.xShiftUnit = defaultXAxisUnit;
    }
    return converted;
  };
  const changeFromProcessedFile = useRef(false);
  const changeFromPreset = useRef(false);
  const [wasChanged, setWasChanged] = useState(false);
  const convertedInitialOptions = convertInitialOptions(single && initialOptions ? initialOptions : {});
  
  const [shiftEnabled, setShiftEnabled] = useState(
    single && initialOptions ? convertedInitialOptions.shift : false
  );
  const [xShift, setXShift] = useState(
    single && initialOptions ? convertedInitialOptions.xShift : "none"
  );
  const [yShift, setYShift] = useState(
    single && initialOptions ? convertedInitialOptions.yShift : "none"
  );
  const [xVal, setXVal] = useState(
    single && initialOptions ? convertedInitialOptions.xShiftVal : ""
  );
  const [xShiftUnit, setXShiftUnit] = useState(
    single && initialOptions ? convertedInitialOptions.xShiftUnit : defaultXAxisUnit
  );
  const [yVal, setYVal] = useState(
    single && initialOptions ? convertedInitialOptions.yShiftVal : ""
  );
  const [xColNum, setXColNum] = useState(1);
  const [yColNum, setYColNum] = useState(2);
  //const [eColNum, setEColNum] = useState("none");
  const [xColNumInput, setXColNumInput] = useState("1");
  const [yColNumInput, setYColNumInput] = useState("2");
  const [cutEnabled, setCutEnabled] = useState(false);
  const [xMin, setXMin] = useState("");
  const [xMax, setXMax] = useState("");
  const [cutUnit, setCutUnit] = useState(defaultXAxisUnit);
  const [reduceFactor, setReduceFactor] = useState(1);
  const [presetName, setPresetName] = useState("");
  const PRESET_NAME_MAX_LENGTH = 10;
  const [additionalColumns, setAdditionalColumns] = useState([]);
  
  // State for additional column shifts
  const [additionalColumnShifts, setAdditionalColumnShifts] = useState([]);

  // Validation for xMin and xMax using isCompleteNumber, but allow empty string
  const xMinValid = xMin === "" || isCompleteNumber(xMin);
  const xMaxValid = xMax === "" || isCompleteNumber(xMax);
  const xMinFloat = parseFloat(xMin);
  const xMaxFloat = parseFloat(xMax);
  const bothFloat = xMin !== "" && xMax !== "" && xMinValid && xMaxValid && !isNaN(xMinFloat) && !isNaN(xMaxFloat);
  const rangeValid = !bothFloat || xMaxFloat > xMinFloat;
  const xMinError = !xMinValid || (bothFloat && !rangeValid);
  const xMaxError = !xMaxValid || (bothFloat && !rangeValid);

  // Validation for reduceFactor: must be integer >= 1
  const reduceFactorValid = /^\d+$/.test(reduceFactor) && parseInt(reduceFactor, 10) >= 1;
  const reduceFactorError = !reduceFactorValid;

  // Validation for column inputs
  const colCountValid = /^\d+$/.test(colCountInput) && parseInt(colCountInput, 10) >= 2 && parseInt(colCountInput, 10) <= 500;
  const colCountError = !colCountValid;
  
  const xColNumValid = /^\d+$/.test(xColNumInput) && parseInt(xColNumInput, 10) >= 1 && parseInt(xColNumInput, 10) <= colCount;
  const xColNumError = !xColNumValid;
  
  const yColNumValid = /^\d+$/.test(yColNumInput) && parseInt(yColNumInput, 10) >= 1 && parseInt(yColNumInput, 10) <= colCount;
  const yColNumError = !yColNumValid;

  // Validation for preset name: max 10 characters and no duplicates
  const presetNameValid = presetName.length <= PRESET_NAME_MAX_LENGTH;
  const presetNameError = !presetNameValid;

  // Check for duplicate preset names (including empty strings)
  const getAllPresetNames = () => {
    const names = [presetName];
    additionalColumns.forEach(col => names.push(col.presetName));
    return names;
  };

  const getPresetNameError = (presetNameToCheck) => {
    if (presetNameToCheck.length > PRESET_NAME_MAX_LENGTH) {
      return "Maximum 10 characters allowed";
    }
    
    return "";
  };

  const isPresetNameDuplicate = (presetNameToCheck) => {
    const allNames = getAllPresetNames();
    const duplicateCount = allNames.filter(name => name === presetNameToCheck).length;
    return duplicateCount > 1;
  };

  // Check if any preset names have errors (length or duplicates)
  const hasPresetNameErrors = () => {
    // Check main preset name
    if (presetName.length > PRESET_NAME_MAX_LENGTH || isPresetNameDuplicate(presetName)) {
      return true;
    }
    
    // Check additional column preset names
    return additionalColumns.some(column => 
      column.presetName.length > PRESET_NAME_MAX_LENGTH || 
      isPresetNameDuplicate(column.presetName)
    );
  };

  // Validation functions for additional columns
  const getAdditionalColumnError = (column, field) => {
    if (field === 'yColNum') {
      const value = column[field];
      if (value === "" || value === undefined) {
        return true; // Empty is invalid
      }
      const numValue = parseInt(value, 10);
      return isNaN(numValue) || numValue < 1 || numValue > colCount;
    }
    return false; // X column is always valid (uses main X column)
  };

  useEffect(() => {
    if (fileContent !== null && processedFileDetails === null) {
      // Take a line from the middle of the file
      const lines = fileContent.split("\n");
      const midIdx = Math.floor(lines.length / 2);
      let line = lines[midIdx] || "";
      line = line.trim();

      let detectedSeparator = "whitespace";
      if (line.includes(";")) {
        detectedSeparator = "semicolon";
      } else if (line.includes(":")) {
        detectedSeparator = "colon";
      } else if (line.includes(",")) {
        // Check for whitespace before or after comma
        const commaMatch = line.match(/\s*,\s*/);
        if (commaMatch) {
          detectedSeparator = "comma";
        } else {
          detectedSeparator = "whitespace";
        }
      }
      setSeparator(detectedSeparator);
      // Detect number of columns in the selected line
      let colCountDetected = 2;
      if (detectedSeparator === "whitespace") {
        colCountDetected = line.split(/\s+/).filter(Boolean).length;
      } else if (detectedSeparator === "comma") {
        colCountDetected = line.split(",").length;
      } else if (detectedSeparator === "semicolon") {
        colCountDetected = line.split(";").length;
      } else if (detectedSeparator === "colon") {
        colCountDetected = line.split(":").length;
      }
      if (colCountDetected > 500) {
        colCountDetected = 500;
      }
      else if (colCountDetected < 2) {
        colCountDetected = 2;
      }
      setColCount(colCountDetected);
      setColCountInput(colCountDetected.toString());
      
      performCheckAfterChange();
    }
  }, [fileContent]);

  useEffect(() => {
    try {
      if (processedFileDetails !== null && processedFileDetails !== undefined) {
        const options = processedFileDetails.options;

        setAutoRemove(options.autoRemove);
        // setCol1(options.col1);
        // setCol2(options.col2);
        // setCol3(options.col3);
        setColCount(Math.min(options.colCount, 500));
        setColCountInput(options.colCount.toString());
        setManualLineStart(options.manualLineStart);
        setSeparator(options.separator);
        setXUnit(options.xUnit);
        // setColumnOptions(options.columns);
        setShiftEnabled(options.shift);
        setXShift(options.xShift);
        setXVal(options.xShiftVal);
        setXShiftUnit(options.xShiftUnit || defaultXAxisUnit);
        setYShift(options.yShift);
        setYVal(options.yShiftVal);
        setCutUnit(options.cutUnit || defaultXAxisUnit);

        changeFromProcessedFile.current = true;
      }
    } catch (error) {
      recordedErrorLog("processedFileDetails useEffect failure: ", error);
    }
  }, [processedFileDetails]);

  useEffect(() => {
    try {
      // We check if file content was filled in and only then we try to check it against options
      if (fileContent !== null) {
        if (changeFromProcessedFile.current) {
          performCheckAfterChange(true);
          changeFromProcessedFile.current = false;
        } else {
          performCheckAfterChange();
        }

        if (changeFromPreset.current) {
          changeFromPreset.current = false;
        } else {
          setWasChanged(true);
        }
      }
    } catch (error) {
      recordedErrorLog("Check after option change failure: ", error);
    }
  }, [
    // fileContent,
    colCount,
    colCountInput,
    xUnit,
    separator,
    autoRemove,
    manualLineStart,
    // columnOptions,
    shiftEnabled,
    yVal,
    yShift,
    xVal,
    xShift,
    xShiftUnit,
    //eColNum,
    yColNum,
    yColNumInput,
    xColNum,
    xColNumInput,
    cutEnabled,
    xMin,
    xMax,
    cutUnit,
    reduceFactor,
    presetName,
    additionalColumns,
    additionalColumnShifts,
  ]);


  // Call onOptionsChange when options change
  useEffect(() => {
    if (onOptionsChange) {
      
      if (fileName && fileName.toLowerCase().includes("tutorial")) {
        const options = generateOptionObject();
        if (options && Object.keys(options).length > 0) {
          monitorStateChange("tutorial_processing_options_changed", {
            options: options,
            fileName: fileName,
            tutorial: currentTutorial,
            shiftEnabled: shiftEnabled,
            cutEnabled: cutEnabled
          });
        }
      }
      // Don't update options if there are preset name errors
      if (hasPresetNameErrors()) {
        return;
      }
      const options = generateOptionObject();
      onOptionsChange(options);
      
      // Monitor processing options changes for tutorial progression
    }
  }, [
    colCount,
    xUnit,
    separator,
    autoRemove,
    manualLineStart,
    shiftEnabled,
    yVal,
    yShift,
    xVal,
    xShift,
    xShiftUnit,
    //eColNum,
    yColNum,
    xColNum,
    cutEnabled,
    xMin,
    xMax,
    cutUnit,
    reduceFactor,
    presetName,
    additionalColumns,
    additionalColumnShifts,
    onOptionsChange,
    monitorStateChange,
    fileIndex,
    fileName,
    currentTutorial,
  ]);

  /**
   * This function is called when user changes the number of columns in the file. It sets the number of columns
   * @function handleColChange
   * @param {Event} e - This is the event that is triggered when user changes the number of columns in the file.
   */
  const handleColChange = (e) => {
    const val = e.target.value;
    if (val === "" || (/^\d+$/.test(val) && parseInt(val, 10) >= 2) && parseInt(val, 10) <= 500) {
      setColCountInput(val);
      if (val !== "") {
        const numVal = parseInt(val, 10);
        setColCount(numVal);
        
        // Adjust X and Y column numbers if they exceed the new column count
        if (xColNum > numVal) {
          setXColNum(1);
          setXColNumInput("1");
        }
        if (yColNum > numVal) {
          setYColNum(1);
          setYColNumInput("1");
        }
      }
    }
  };

  /**
   * This function is called when user changes the separator of the file. It sets the separator.
   * @function handleSepChange
   * @param {Event} e - This is the event that is triggered when user changes the separator of the file.
   */
  const handleSepChange = (e) => {
    setSeparator(e.target.value);
  };

  /**
   * This function is called when user changes the value of a column. It sets the value of the column and
   * conditionally changes the value of the other columns.
   * @function handleColValChange
   * @param {Event} e - This is the event that is triggered when user changes the value of a column.
   * @param {number} col - This is the number of the column that is being changed.
   */
  // const handleColValChange = (e, col) => {
  //   const valToSet = e.target.value;
  //   switch (col) {
  //     case 1:
  //       if (col2 === valToSet) {
  //         setCol2(col1);
  //       } else {
  //         setCol3(col1);
  //       }
  //       setCol1(valToSet);
  //       break;
  //     case 2:
  //       if (col1 === valToSet) {
  //         setCol1(col2);
  //       } else {
  //         setCol3(col2);
  //       }
  //       setCol2(valToSet);
  //       break;
  //     case 3:
  //       if (col2 === valToSet) {
  //         setCol2(col3);
  //       } else {
  //         setCol1(col3);
  //       }
  //       setCol3(valToSet);
  //       break;

  //     default:
  //       break;
  //   }
  // };

  /**
   * This function generates a list of menu items that can be used to select the value of a column.
   * @function getColValSelectOptions
   * @returns {JSX.Element[]} Returns a list of menu items that can be used to select the value of a column.
   */
  // const getColValSelectOptions = () => {
  //   // Generate menu items dynamically based on colCount
  //   const menuItems = [
  //     <MenuItem key={"x"} value={"x"} className="menuItem">
  //       x
  //     </MenuItem>,
  //     <MenuItem key={"y"} value={"y"} className="menuItem">
  //       y
  //     </MenuItem>,
  //   ];

  //   // Conditionally add the third option
  //   if (colCount === 3) {
  //     menuItems.push(
  //       <MenuItem key={"e"} value={"e"} className="menuItem">
  //         e
  //       </MenuItem>
  //     );
  //   }

  //   return menuItems;
  // };

  /**
   * This function is called when user changes the line number. It sets the line number.
   * @function handleLineChange
   * @param {Event} e - This is the event that is triggered when user changes the line number.
   */
  const handleLineChange = (e) => {
    const lineVal = e.target.value;

    if (lineVal < 0) {
      setManualLineStart(0);
    } else {
      setManualLineStart(lineVal);
    }
  };

  /**
   * This function is called when user changes the unit of the x-data unit. It sets the unit of the x-data unit.
   * @function handleUnitChange
   * @param {Event} e - This is the event that is triggered when user changes the unit of the x-data unit.
   */
  const handleUnitChange = (e) => {
    const value = e.target.value;
    const unit = units.filter((u) => u.value === value)[0];
    setXUnit(unit);
  };

  const handleXShiftUnitChange = (e) => {
    const value = e.target.value;
    const unit = units.filter((u) => u.value === value)[0];
    setXShiftUnit(unit);
  };

  const handleCutUnitChange = (e) => {
    const value = e.target.value;
    const unit = units.filter((u) => u.value === value)[0];
    setCutUnit(unit);
  };

  /**
   * This function generates an object containing the options for processing the file.
   * @function generateOptionObject
   * @returns {object} Returns an object containing the options for processing the file.
   */
  const generateOptionObject = () => {
    try {
      return {
        colCount: colCount,
        xUnit: xUnit,
        separator: separator,
        autoRemove: autoRemove,
        manualLineStart: manualLineStart,
        // columns: columnOptions,
        //eColNum: eColNum,
        yColNum: yColNum,
        xColNum: xColNum,
        shift: shiftEnabled,
        xShift: xShift,
        yShift: yShift,
        xShiftVal: xVal,
        xShiftUnit: xShiftUnit,
        yShiftVal: yVal,
        cut: cutEnabled,
        xMin: xMin,
        xMax: xMax,
        cutUnit: cutUnit,
        reduceFactor: reduceFactor,
        presetName: presetName,
        additionalColumns: additionalColumns,
        additionalColumnShifts: additionalColumnShifts,
      };
    } catch (error) {
      recordedErrorLog("Options object generation failure: ", error);
    }
  };

  /**
   * This function perfoms a check to determined if selected options are working with the file.
   * @function performCheckAfterChange
   */
  const performCheckAfterChange = (skipSolveClear = false) => {
    try {
      // Don't process if there are preset name errors
      if (hasPresetNameErrors()) {
        // Clear the processed content to show that processing is invalid
        check(null, skipSolveClear);
        return;
      }
      check(generateOptionObject(), skipSolveClear);
    } catch (error) {
      recordedErrorLog("Check after change failure: ", error);
    }
  };

  /**
   * This function is called when user loads a preset. It sets the options based on the preset.
   * @function handlePresetLoad
   * @param {number} id - This is the id of the preset that is being loaded.
   */
  const handlePresetLoad = (id) => {
    try {
      changeFromPreset.current = true;
      const selectedPresetLoadingOptions = filePresetsLoadingOptions.find((preset) => preset.id === id);

      setColCount(selectedPresetLoadingOptions.presetOptions.colCount);
      setColCountInput(selectedPresetLoadingOptions.presetOptions.colCount.toString());
      setXUnit(selectedPresetLoadingOptions.presetOptions.xUnit);
      setSeparator(selectedPresetLoadingOptions.presetOptions.separator);
      setAutoRemove(selectedPresetLoadingOptions.presetOptions.autoRemove);
      setManualLineStart(selectedPresetLoadingOptions.presetOptions.manualLineStart);
      // setCol1(selectedPresetLoadingOptions.presetOptions.col1);
      // setCol2(selectedPresetLoadingOptions.presetOptions.col2);
      // setCol3(selectedPresetLoadingOptions.presetOptions.col3);
      // setColumnOptions(selectedPresetLoadingOptions.presetOptions.columns);
      setShiftEnabled(selectedPresetLoadingOptions.presetOptions.shift);
      setXShift(selectedPresetLoadingOptions.presetOptions.xShift);
      setXVal(selectedPresetLoadingOptions.presetOptions.xShiftVal);
      setXShiftUnit(selectedPresetLoadingOptions.presetOptions.xShiftUnit || defaultXAxisUnit);
      setYShift(selectedPresetLoadingOptions.presetOptions.yShift);
      setYVal(selectedPresetLoadingOptions.presetOptions.yShiftVal);
      setCutUnit(selectedPresetLoadingOptions.presetOptions.cutUnit || defaultXAxisUnit);
      setPresetName(selectedPresetLoadingOptions.presetOptions.presetName || "");
      setAdditionalColumns(selectedPresetLoadingOptions.presetOptions.additionalColumns || []);
      setAdditionalColumnShifts(selectedPresetLoadingOptions.presetOptions.additionalColumnShifts || []);
    } catch (error) {
      recordedErrorLog("Preset load handler failure: ", error);
    }
  };

  /**
   * This function is called when user deletes a preset. It deletes the preset from the list of presets.
   * @function handleEntryDelete
   * @param {number} id - This is the id of the preset that is being deleted.
   */
  const handleEntryDelete = (id) => {
    try {
      const clearedPresets = filePresetsLoadingOptions.filter((preset) => preset.id !== id);
      setfilePresetsLoadingOptions(clearedPresets);
    } catch (error) {
      recordedErrorLog("Entry delete handler failure: ", error);
    }
  };

  /**
   * This function is called when user saves a preset. It saves the preset to the list of presets.
   * @function savePreset
   */
  const savePreset = () => {
    try {
      save(generateOptionObject());
    } catch (error) {
      recordedErrorLog("Preset saving failure: ", error);
    }
  };

  const handleXColNumChange = (e) => {
    const val = e.target.value;
    if (val === "" || (/^\d+$/.test(val) && parseInt(val, 10) >= 1 && parseInt(val, 10) <= colCount)) {
      setXColNumInput(val);
      if (val !== "") {
        const numVal = parseInt(val, 10);
        setXColNum(numVal);
      }
    }
  };

  const handleYColNumChange = (e) => {
    const val = e.target.value;
    if (val === "" || (/^\d+$/.test(val) && parseInt(val, 10) >= 1 && parseInt(val, 10) <= colCount)) {
      setYColNumInput(val);
      if (val !== "") {
        const numVal = parseInt(val, 10);
        setYColNum(numVal);
      }
    }
  };

  const handlePresetNameChange = (e) => {
    const val = e.target.value;
    if (val.length <= PRESET_NAME_MAX_LENGTH) {
      setPresetName(val);
    }
  };

  const handleLoadAdditionalColumn = () => {
    // Calculate the next Y value based on the last additional column, or main Y if no additional columns exist
    let nextYColNum;
    if (additionalColumns.length > 0) {
      const lastColumn = additionalColumns[additionalColumns.length - 1];
      nextYColNum = lastColumn.yColNum >= colCount ? colCount : lastColumn.yColNum + 1;
            } else {
      nextYColNum = yColNum >= colCount ? colCount : yColNum + 1;
    }

    const newColumn = {
      id: Date.now(), // Simple unique ID
      yColNum: nextYColNum,
      presetName: ""
    };
    setAdditionalColumns(prev => [...prev, newColumn]);
  };

  const handleLoadAllColumns = () => {
    // Update existing additional columns with empty prefixes
    setAdditionalColumns(prev => 
      prev.map(col => {
        if (col.yColNum && col.yColNum >= 2 && (!col.presetName || col.presetName.trim() === "")) {
          return {
            ...col,
            presetName: `Y${col.yColNum}`
          };
        }
        return col;
      })
    );

    // Update main preset name if empty and yColNum is set
    if (yColNum && yColNum >= 2 && (!presetName || presetName.trim() === "")) {
      setPresetName(`Y${yColNum}`);
    }

    // Get existing column numbers to avoid duplicates
    const existingYColNums = new Set();
    if (yColNum && yColNum >= 2) {
      existingYColNums.add(yColNum);
    }
    additionalColumns.forEach(col => {
      if (col.yColNum && col.yColNum >= 2) {
        existingYColNums.add(col.yColNum);
      }
    });

    // Create columns for all columns from 2 to colCount that don't already exist
    const baseId = Date.now();
    const newColumns = [];
    for (let colNum = 2; colNum <= colCount; colNum++) {
      if (!existingYColNums.has(colNum)) {
        newColumns.push({
          id: baseId + colNum, // Simple unique ID
          yColNum: colNum,
          presetName: `Y${colNum}`
        });
      }
    }

    if (newColumns.length > 0) {
      setAdditionalColumns(prev => [...prev, ...newColumns]);
    }
  };

  const handleAdditionalColumnChange = (columnId, field, value) => {
    // Only handle Y column numbers and other fields (X is always the main X column)
    if (field === 'yColNum') {
      const numValue = parseInt(value, 10);
      // Only allow empty string or valid numbers within range
      if (value === "" || (!isNaN(numValue) && numValue >= 1 && numValue <= colCount)) {
        setAdditionalColumns(prev => 
          prev.map(col => 
            col.id === columnId 
              ? { ...col, [field]: value === "" ? "" : numValue }
              : col
          )
        );
      }
    } else {
      // For other fields (like presetName), allow any value
      setAdditionalColumns(prev => 
        prev.map(col => 
          col.id === columnId 
            ? { ...col, [field]: value }
            : col
        )
      );
    }
  };

  const handleAdditionalColumnRemove = (columnId) => {
    setAdditionalColumns(prev => prev.filter(col => col.id !== columnId));
    // Also remove the corresponding shift settings
    setAdditionalColumnShifts(prev => prev.filter(shift => shift.columnId !== columnId));
  };

  const handleAdditionalColumnShiftChange = (columnId, field, value) => {
    setAdditionalColumnShifts(prev => {
      const existingIndex = prev.findIndex(shift => shift.columnId === columnId);
      if (existingIndex >= 0) {
        // Update existing shift
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], [field]: value };
        return updated;
      } else {
        // Create new shift with defaults
        return [...prev, {
          columnId,
          yShift: "none",
          yVal: "",
          [field]: value
        }];
      }
    });
  };

  const renderColumnOptions = () => {
    try {
      // const optionList = [];

      // if (columnOptions && columnOptions.length > 0) {
      //   for (let i = 0; i < colCount; i++) {
      //     optionList.push(
      //       <div className="columnOptionDropdown" key={i}>
      //         <div className="colNumber">{i + 1}:</div>
      // <FormControl variant="standard" className="formControl">
      //   <Select
      //     MenuProps={{
      //       container: () => document.getElementById(modalId),
      //     }}
      //     value={columnOptions[i]}
      //     onChange={(e) => handleColOptionChange(i, e.target.value)}
      //     className="select"
      //   >
      //     <MenuItem key={"x"} value={"x"} className="menuItem">
      //       x
      //     </MenuItem>
      //     <MenuItem key={"y"} value={"y"} className="menuItem">
      //       y
      //     </MenuItem>
      //     {colCount > 2 && (
      //       <MenuItem key={"e"} value={"e"} className="menuItem">
      //         e
      //       </MenuItem>
      //     )}
      //     {colCount > 2 && (
      //       <MenuItem
      //         key={"ignore"}
      //         value={"ignore"}
      //         className="menuItem"
      //       >
      //         Ignore
      //       </MenuItem>
      //     )}
      //   </Select>
      // </FormControl>
      //       </div>
      //     );
      //   }
      // }

      // return optionList;

      return (
        <div className="columnSelector">
          <div className="singleColumn">
            <div className="columnName">X:</div>
            <TextField
              variant="standard"
              size="small"
              type="number"
              value={xColNumInput}
              onChange={handleXColNumChange}
              inputProps={{ min: 1, max: colCount }}
              className="select mid"
              error={xColNumError}
            />
          </div>
          <div className="singleColumn">
            <div className="columnName">Y:</div>
            <TextField
              variant="standard"
              size="small"
              type="number"
              value={yColNumInput}
              onChange={handleYColNumChange}
              inputProps={{ min: 1, max: colCount }}
              className="select mid"
              error={yColNumError}
              id="processing-options-y-input"
            />
          </div>
          {!single && (
            <div className="singleColumn">
              <div className="columnName">Prefix:</div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  variant="standard"
                  size="small"
                  value={presetName}
                  onChange={handlePresetNameChange}
                  placeholder="Enter prefix"
                  className={`select big ${presetNameError || isPresetNameDuplicate(presetName) ? 'presetFieldError' : ''}`}
                  helperText={getPresetNameError(presetName)}
                  id="processing-options-prefix-input"
                />
                {(presetNameError || isPresetNameDuplicate(presetName)) && (
                  <HtmlTooltip 
                    title={
                      <div style={{ 
                        backgroundColor: '#333', 
                        color: 'white', 
                        padding: '8px', 
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        Prefix names should be unique
                      </div>
                    }
                  >
                    <HelpOutlineIcon 
                      style={{ 
                        marginLeft: '5px', 
                        color: '#e90202', 
                        cursor: 'pointer',
                        fontSize: '18px'
                      }} 
                    />
                  </HtmlTooltip>
                )}
              </div>
            </div>
          )}
        </div>
      );
    } catch (error) {
      recordedErrorLog("Column option rendered has failed: ", error);
    }
  };

  return (
    <div className="processingOptionsSection">
      <div className="optionsSection">
        <div className="optionsArea">
          <div className="optionsIntro">Data Loading Options:</div>
          {processedContent ? (
            <div className="optionRow">
              <div className="optionTitle">Total data points:&nbsp;</div>
              <div>{processedContent.length}</div>
            </div>
          ) : (
            <></>
          )}
          <div className="optionRow">
            <div className="optionTitle">X-Data Unit:</div>
            <FormControl variant="standard" className="formControl">
              <Select
                MenuProps={{
                  container: () => document.getElementById(modalId),
                }}
                labelId="selectUnit"
                value={xUnit.value}
                onChange={handleUnitChange}
                className="select"
                id="processing-options-x-data-unit-select"
              >
                {units.map(({ name, value }) => (
                  <MenuItem key={name} value={value} id={`processing-options-x-data-unit-${value === "cm^-1" ? "cm-1" : value}`}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <HtmlTooltip 
              title={
                <div style={{ 
                  backgroundColor: '#333', 
                  color: 'white', 
                  padding: '8px', 
                  borderRadius: '4px',
                  fontSize: '12px'
                }}>
                  File units will be translated to cm-1 before showing in the graphs
                </div>
              }
            >
              <div className="extraInfo">
              <sup>
                <HelpOutlineIcon className="infoIcon" />
              </sup>
            </div>
            </HtmlTooltip>
          </div>
          {!single && (
            <>
              <div className="optionRow">
                <div className="optionTitle">Number of Columns:</div>
                <TextField
                  variant="standard"
                  size="small"
                  type="number"
                  value={colCountInput}
                  onChange={handleColChange}
                  inputProps={{ min: 2, max: 500 }}
                  className="select mid"
                  error={colCountError}
                />
              </div>
              <div className="optionRow">
                <div className="optionTitle">Separated by:</div>
                <FormControl variant="standard" className="formControl">
                  <Select
                    MenuProps={{
                      container: () => document.getElementById(modalId),
                    }}
                    labelId="group-select-label"
                    value={separator}
                    onChange={handleSepChange}
                    className="select big"
                  >
                    <MenuItem key={"whitespace"} value={"whitespace"} className="menuItem">
                      Whitespace
                    </MenuItem>
                    <MenuItem
                      key={"comma"}
                      value={"comma"}
                      className="menuItem"
                    >
                      Comma
                    </MenuItem>
                    <MenuItem
                      key={"semicolon"}
                      value={"semicolon"}
                      className="menuItem"
                    >
                      Semicolon
                    </MenuItem>
                    <MenuItem
                      key={"colon"}
                      value={"colon"}
                      className="menuItem"
                    >
                      Colon
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            </>
          )}
          {/* <div className="optionRow">
            <div className="optionTitle">Column 1:</div>
            <FormControl variant="standard" className="formControl">
              <Select
                MenuProps={{
                  container: () => document.getElementById(modalId),
                }}
                labelId="group-select-label"
                value={col1}
                onChange={(e) => handleColValChange(e, 1)}
                className="select min"
              >
                {getColValSelectOptions()}
              </Select>
            </FormControl>
          </div>
          <div className="optionRow">
            <div className="optionTitle">Column 2:</div>
            <FormControl variant="standard" className="formControl">
              <Select
                MenuProps={{
                  container: () => document.getElementById(modalId),
                }}
                labelId="group-select-label"
                value={col2}
                onChange={(e) => handleColValChange(e, 2)}
                className="select min"
              >
                {getColValSelectOptions()}
              </Select>
            </FormControl>
          </div> */}
          {/* {colCount === 3 ? (
            <div className="optionRow">
              <div className="optionTitle">Column 3:</div>
              <FormControl variant="standard" className="formControl">
                <Select
                  MenuProps={{
                    container: () => document.getElementById(modalId),
                  }}
                  labelId="group-select-label"
                  value={col3}
                  onChange={(e) => handleColValChange(e, 3)}
                  className="select min"
                >
                  <MenuItem key={"x"} value={"x"} className="menuItem">
                    x
                  </MenuItem>
                  <MenuItem key={"y"} value={"y"} className="menuItem">
                    y
                  </MenuItem>
                  <MenuItem key={"e"} value={"e"} className="menuItem">
                    e
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
          ) : (
            <></>
          )} */}
          {!single && (
            <>
              <div className="optionRow vertical">
                <div className="title">Columns:</div>
                <div className="columnOptions">{renderColumnOptions()}</div>
                {additionalColumns.map((column) => (
                  <div key={column.id} className="additionalColumnRow">
                    <div className="singleColumn">
                      <div className="columnName">X:</div>
                      <div className="select mid" style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        height: '32px',
                      }}>
                        {xColNum}
                      </div>
                    </div>
                    <div className="singleColumn">
                      <div className="columnName">Y:</div>
                      <TextField
                        variant="standard"
                        size="small"
                        type="number"
                        value={column.yColNum}
                        onChange={(e) => handleAdditionalColumnChange(column.id, 'yColNum', e.target.value)}
                        inputProps={{ min: 1, max: colCount }}
                        className="select mid"
                        error={getAdditionalColumnError(column, 'yColNum')}
                        id={`additional-column-y-input`}
                      />
                    </div>
                    <div className="singleColumn">
                      <div className="columnName">Prefix:</div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                          variant="standard"
                          size="small"
                          value={column.presetName}
                          onChange={(e) => handleAdditionalColumnChange(column.id, 'presetName', e.target.value)}
                          placeholder="Enter prefix"
                          className={`select big ${isPresetNameDuplicate(column.presetName) ? 'presetFieldError' : ''}`}
                          helperText={getPresetNameError(column.presetName)}
                          id={`additional-column-prefix-input`}
                        />
                        {isPresetNameDuplicate(column.presetName) && (
                          <HtmlTooltip 
                            title={
                              <div style={{ 
                                backgroundColor: '#333', 
                                color: 'white', 
                                padding: '8px', 
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}>
                                Prefix names should be unique
                              </div>
                            }
                          >
                            <HelpOutlineIcon 
                              style={{ 
                                marginLeft: '5px', 
                                color: '#e90202', 
                                cursor: 'pointer',
                                fontSize: '18px'
                              }} 
                            />
                          </HtmlTooltip>
                        )}
                      </div>
                    </div>
                    <div className="singleColumn">
                      <CustomButton
                        text="Remove"
                        extraClassnames={["noMargin", "dataManagerButton"]}
                        handleClick={() => handleAdditionalColumnRemove(column.id)}
                        id={`remove-additional-column-button`}
                      />
                    </div>
                  </div>
                ))}
                {colCount > 2 && additionalColumns.length < colCount - 1 && (
                  <div className="additionalColumnButton" style={{ display: "flex", gap: "10px" }}>
                    <CustomButton
                      text="Load Additional Column"
                      extraClassnames={["noMargin", "dataManagerButton"]}
                      handleClick={handleLoadAdditionalColumn}
                      id="load-additional-column-button"
                    />
                    <CustomButton
                      text="Load All Columns"
                      extraClassnames={["noMargin", "dataManagerButton"]}
                      handleClick={handleLoadAllColumns}
                      id="load-all-columns-button"
                    />
                  </div>
                )}
              </div>
              <div className="optionRow">
                <FormControl
                  sx={{
                    width: "250px",
                    userSelect: "none",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={autoRemove}
                        onChange={() => setAutoRemove((old) => !old)}
                      />
                    }
                    label={"Auto-Remove file header"}
                  />
                </FormControl>
              </div>
              {!autoRemove && (
                <div className="optionRow">
                  <div className="optionTitle">Data starts at line:</div>
                  <TextField
                    variant="standard"
                    size="small"
                    type="number"
                    value={manualLineStart}
                    onChange={handleLineChange}
                    className="select mid"
                  />
                </div>
              )}
            </>
          )}
          <div className="optionRow">
            <FormControl
              sx={{
                width: "250px",
                userSelect: "none",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shiftEnabled}
                    onChange={() => setShiftEnabled((old) => !old)}
                    id="shift-or-scale-data-points-checkbox"
                  />
                }
                label={"Shift or scale data points"}
              />
            </FormControl>
          </div>
          {shiftEnabled ? (
            <div className="optionRow vertical">
              <div className="shiftRow">
                <div className="title">X-axis:</div>
                <FormControl variant="standard" className="formControl">
                  <Select
                    MenuProps={{
                      container: () => document.getElementById(modalId),
                    }}
                    value={xShift}
                    onChange={(e) => setXShift(e.target.value)}
                    className="select big"
                  >
                    <MenuItem key={"none"} value={"none"} className="menuItem">
                      No shift
                    </MenuItem>
                    <MenuItem
                      key={"val-x"}
                      value={"val-x"}
                      className="menuItem"
                    >
                      Value - X
                    </MenuItem>
                    <MenuItem
                      key={"x+val"}
                      value={"x+val"}
                      className="menuItem"
                    >
                      X + Value
                    </MenuItem>
                    <MenuItem
                      key={"x*val"}
                      value={"x*val"}
                      className="menuItem"
                    >
                      X * Value
                    </MenuItem>
                  </Select>
                </FormControl>
                <div className="valTitle">Value:</div>
                <input
                  type="number"
                  className="valueInput"
                  value={xVal}
                  onChange={(e) => setXVal(e.target.value)}
                />
                <div className="valTitle">Unit:</div>
                <FormControl variant="standard" className="formControl">
                  <Select
                    MenuProps={{
                      container: () => document.getElementById(modalId),
                    }}
                    value={xShiftUnit.value}
                    onChange={handleXShiftUnitChange}
                    className="select"
                  >
                    {units.map(({ name, value }) => (
                      <MenuItem key={value} value={value}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HtmlTooltip 
                  title={
                    <div style={{ 
                      backgroundColor: '#333', 
                      color: 'white', 
                      padding: '8px', 
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      The units this shift/scale is applied in.
                    </div>
                  }
                >
                  <HelpOutlineIcon 
                    style={{ 
                      marginLeft: '5px', 
                      cursor: 'pointer',
                      fontSize: '18px'
                    }} 
                  />
                </HtmlTooltip>
              </div>
              <div className="shiftRow">
                <div className="title">Y-axis:</div>
                <FormControl variant="standard" className="formControl">
                  <Select
                    MenuProps={{
                      container: () => document.getElementById(modalId),
                    }}
                    value={yShift}
                    onChange={(e) => setYShift(e.target.value)}
                    className="select big"
                    id="processing-options-column-shift-select"
                  >
                    <MenuItem key={"none"} value={"none"} className="menuItem">
                      No shift
                    </MenuItem>
                    <MenuItem
                      key={"val-x"}
                      value={"val-x"}
                      className="menuItem"
                      id="processing-options-column-shift-val-x"
                    >
                      Value - Y
                    </MenuItem>
                    <MenuItem
                      key={"x+val"}
                      value={"x+val"}
                      className="menuItem"
                      id="processing-options-column-shift-x+val"
                    >
                      Y + Value
                    </MenuItem>
                    <MenuItem
                      key={"x*val"}
                      value={"x*val"}
                      className="menuItem"
                      id="processing-options-column-shift-xval"
                    >
                      Y * Value
                    </MenuItem>

                  </Select>
                </FormControl>
                <div className="valTitle">Value:</div>
                <input
                  type="number"
                  className="valueInput"
                  value={yVal}
                  onChange={(e) => setYVal(e.target.value)}
                  id="processing-options-column-shift-value-input"
                />
                <div className="valTitle">{presetName && presetName.trim() !== "" ? presetName : ""}</div>
              </div>
              {/* Additional column shifts */}
              {additionalColumns.map((column) => {
                const shiftSettings = additionalColumnShifts.find(shift => shift.columnId === column.id) || {
                  yShift: "none",
                  yVal: ""
                };
                const displayName = column.presetName && column.presetName.trim() !== "" 
                  ? column.presetName 
                  : "";
                
                return (
                  <div key={`shift-${column.id}`} className="shiftRow">
                    <div className="title">Y-axis:</div>
                    <FormControl variant="standard" className="formControl">
                      <Select
                        MenuProps={{
                          container: () => document.getElementById(modalId),
                        }}
                        value={shiftSettings.yShift}
                        onChange={(e) => handleAdditionalColumnShiftChange(column.id, 'yShift', e.target.value)}
                        className="select big"
                        id={`additional-column-shift-select`}
                      >
                        <MenuItem key={"none"} value={"none"} className="menuItem">
                          No shift
                        </MenuItem>
                        <MenuItem
                          key={"val-x"}
                          value={"val-x"}
                          className="menuItem"
                          id={`additional-column-shift-val-x`}
                        >
                          Value - Y
                        </MenuItem>
                        <MenuItem
                          key={"x+val"}
                          value={"x+val"}
                          className="menuItem"
                          id={`additional-column-shift-x+val`}
                        >
                          Y + Value
                        </MenuItem>
                        <MenuItem
                          key={"x*val"}
                          value={"x*val"}
                          className="menuItem"
                          id={`additional-column-shift-xval`}
                        >
                          Y * Value
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <div className="valTitle">Value:</div>
                    <input
                      type="number"
                      className="valueInput"
                      value={shiftSettings.yVal}
                      id={`additional-column-shift-value-input`}
                      onChange={(e) => handleAdditionalColumnShiftChange(column.id, 'yVal', e.target.value)}
                    />
                    <div className="valTitle">{displayName}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <></>
          )}
          <div className="optionRow">
            <FormControl
              sx={{
                width: "250px",
                userSelect: "none",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cutEnabled}
                    onChange={() => setCutEnabled((old) => !old)}
                  />
                }
                label={"Pre-cut data"}
              />
            </FormControl>
          </div>
          {cutEnabled ? (
            <div className="optionRow vertical">
              <div className="cutRow">
                <div className="cutRowItem">
                  <div className="title">X Min:</div>
                  <input
                    type="number"
                    className="valueInput"
                    value={xMin}
                    onChange={e => {
                      const val = e.target.value;
                      if (val === "" || isCompleteNumber(val)) {
                        setXMin(val);
                      } else {
                        setXMin("");
                      }
                    }}
                    placeholder=""
                    style={xMinError ? { border: '2px solid red' } : {}}
                  />
                  <div className="valTitle">Unit:</div>
                  <FormControl variant="standard" className="formControl">
                    <Select
                      MenuProps={{
                        container: () => document.getElementById(modalId),
                      }}
                      value={cutUnit.value}
                      onChange={handleCutUnitChange}
                      className="select"
                    >
                      {units.map(({ name, value }) => (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="cutRowItem">
                  <div className="title">X Max:</div>
                  <input
                    type="number"
                    className="valueInput"
                    value={xMax}
                    onChange={e => {
                      const val = e.target.value;
                      if (val === "" || isCompleteNumber(val)) {
                        setXMax(val);
                      } else {
                        setXMax("");
                      }
                    }}
                    placeholder=""
                    style={xMaxError ? { border: '2px solid red' } : {}}
                  />
                  <div className="valTitle">Unit:</div>
                  <FormControl variant="standard" className="formControl">
                    <Select
                      MenuProps={{
                        container: () => document.getElementById(modalId),
                      }}
                      value={cutUnit.value}
                      onChange={handleCutUnitChange}
                      className="select"
                    >
                      {units.map(({ name, value }) => (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="title">Reduce data by factor:</div>
                <input
                  type="number"
                  className="valueInput"
                  value={reduceFactor}
                  onChange={e => {
                    const val = e.target.value;
                    if (val === "" || (/^\d+$/.test(val) && parseInt(val, 10) >= 1)) {
                      setReduceFactor(val);
                    } else {
                      setReduceFactor("");
                    }
                  }}
                  placeholder="1"
                  style={reduceFactorError ? { border: '2px solid red' } : {}}
                />
              </div>
            </div>
          ) : null}
        </div>
        <div className="optionLoadingArea">
          <div className="loadingAreaTitle">Saved Loading Options:</div>
          <div className="presetLitContainer">
            <PresetLoader
              presetList={filePresetsLoadingOptions}
              load={handlePresetLoad}
              deleteEntry={handleEntryDelete}
              wasChanged={wasChanged}
              setWasChanged={setWasChanged}
            />
          </div>
        </div>
      </div>

      <div className="buttonArea">
        <CustomButton
          text="Apply"
          extraClassnames={["noMargin", "dataManagerButton"]}
          handleClick={() => apply(generateOptionObject())}
          id="data-apply-button"
          disabled={!processedContent || processedContent === "" || hasPresetNameErrors()}
        />
        {/* <Button
          id="data-apply-button"
          variant="contained"
          className="solveButton"
          onClick={() => apply(generateOptionObject())}
        >
          Apply
        </Button> */}
        {!single ? (
          <CustomButton
            text="Apply to All"
            extraClassnames={["noMargin", "dataManagerButton"]}
            handleClick={() => applyAll(generateOptionObject())}
            id="data-apply-to-all-button"
            disabled={!processedContent || processedContent === "" || hasPresetNameErrors()}
          />
        ) : (
          // <Button
          //   variant="contained"
          //   className="solveButton"
          //   onClick={() => applyAll(generateOptionObject())}
          // >
          //   Apply to All
          // </Button>
          <></>
        )}
        <CustomButton
          text="Save Loading Options"
          extraClassnames={["noMargin", "dataManagerButton"]}
          handleClick={savePreset}
        />
        {/* <Button
          variant="contained"
          className="loadPresetButton"
          onClick={() => savePreset()}
        >
          Save as Preset
        </Button> */}
        {single ? (
          // <Button
          //   variant="contained"
          //   className="solveButton"
          //   onClick={() => cancel()}
          // >
          //   Cancel
          // </Button>
          <CustomButton
            text="Cancel"
            extraClassnames={["noMargin", "dataManagerButton"]}
            handleClick={cancel}
          />
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}

export default ProcessingOptions;
